import React, { useContext, useEffect } from 'react';
import { useFormikContext } from 'formik';

import { FormContext } from 'contexts';
import { AuFormGroup } from 'components/DTA';
import {
  StepContainer,
  FormNavigation,
  ApplicationName
} from 'components/CASAForm';
import { DroneRegistrationFormData } from 'types/forms/droneRegistrationTypes';
import { IFormAlertType } from 'types/forms/formTypes';
import { Button } from 'components/common';

const DroneDetailsStep: React.FC<{ droneIndex: number; }> = ({ droneIndex }) => {
  const { setFormAlert, openStepById } = useContext(FormContext);
  const { values } = useFormikContext<DroneRegistrationFormData>();

  const updateDrone = () => {
    openStepById('DroneEdit', { droneIndex });
  };

  // Add page alerts
  useEffect(() => {
    if (values.drones[droneIndex].modelUpdateRequired || values.drones[droneIndex].serialNumberUpdateRequired) {
      const alertMessage =
        <>
          {values.drones[droneIndex].modelUpdateRequired &&
            <>
              CASA no longer accepts &apos;Other&apos; as a model for this manufacturer. You will need to select the specific model for this drone before it can be renewed.
              <br />
              <br />
            </>
          }
          {values.drones[droneIndex].serialNumberUpdateRequired &&
            <>
              {values.drones[droneIndex].serialNumber?.startsWith('CASA-') &&
                <>CASA no longer accepts the use of a CASA registration mark for this manufacturer. You will need to provide the correct serial number for this drone before it can be renewed.</>
              }
              {!values.drones[droneIndex].serialNumber?.startsWith('CASA-') &&
                <>Please check your drone&apos;s serial number and try again. You can find guidance on finding the correct serial number on the <a href='https://www.casa.gov.au/drones/register/serial-numbers' target='_blank' rel='noopener noreferrer'>CASA website</a></>
              }
              <br />
              <br />
            </>
          }
          <Button as='primary' onClick={updateDrone}>Update drone</Button>
        </>;
      const alert: IFormAlertType = {
        as: 'warning',
        title: 'Updates required',
        message: alertMessage
      };
      setFormAlert(alert);
    }
  }, []);

  return (
    <>
      <ApplicationName name='Renew drone registration' />
      <StepContainer
        title='Drone details'
        customHandleBack={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          setFormAlert(null);
          openStepById('DroneRenewalSummary');
        }}
      >
        <div className='no-gutters row'>
          <div className='float-none col-md-12 col-lg-12'>
            <AuFormGroup>
              <h2 className='au-display-md'>Manufacturer and model</h2>
              {values.drones[droneIndex].manufacturer} {values.drones[droneIndex].model}{values.drones[droneIndex].modelUpdateRequired ? <span style={{ marginLeft: '.2rem' }} className='badge badge-update'>Updates required</span> : ''}
              <h2 className='au-display-md'>Serial number / registration mark</h2>
              {values.drones[droneIndex].serialNumber}{values.drones[droneIndex].serialNumberUpdateRequired ? <span style={{ marginLeft: '.2rem' }} className='badge badge-update'>Updates required</span> : ''}
              <h2 className='au-display-md'>Weight class</h2>
              {values.drones[droneIndex].weightClass}
              <h2 className='au-display-md'>Registration type</h2>
              {values.drones[droneIndex].intendedUse === 'commercial' ? 'Commercial' : 'Recreational'}
              <h2 className='au-display-md'>Expiry date</h2>
              {values.drones[droneIndex].validUntil}{values.drones[droneIndex].validUntilMessage && values.drones[droneIndex].validUntilMessageLevel ? <span style={{ marginLeft: '.2rem' }} className={`badge badge-${values.drones[droneIndex].validUntilMessageLevel}`}>{values.drones[droneIndex].validUntilMessage}</span> : ''}
            </AuFormGroup>
          </div>
        </div>

        <FormNavigation
          hidePrevious
          hideNext
          showSaveAndExit={false} />
      </StepContainer>
    </>
  );
};

export default DroneDetailsStep;
